import { json, type ActionFunctionArgs } from '@remix-run/node';
import { z } from 'zod';
import { updateConversationThumbnail } from '~/modules/chat/api/update-conversation-thumbnail.server';
import { validateChatThumbnailUploadFile } from '~/modules/chat/utils/validation';

export const UPDATE_CONVERSATION_THUMBNAIL_API =
    '/remix-api/chat/update-thumbnail';

const FORM_DATA_SCHEMA = z.object({
    conversationID: z.coerce.number().int().positive(),
    professionalID: z.coerce.number().int().positive().nullish(),
    thumbnail: z
        .instanceof(File)
        .refine(
            (file) => {
                const result = validateChatThumbnailUploadFile(file);
                return result.valid;
            },
            { message: 'Invalid file' },
        )
        .nullish(),
});

/**
 * API endpoint to update the thumbnail of a conversation. Use with the `useUpdateConversation` hook.
 * @see {@link packages/web/app/modules/chat/hooks/use-update-conversation.ts}
 */
export const action = async ({ context, request }: ActionFunctionArgs) => {
    const { logger } = context;
    const formData = await request.formData();
    const result = FORM_DATA_SCHEMA.safeParse({
        conversationID: formData.get('conversationID'),
        professionalID: formData.get('professionalID'),
        thumbnail: formData.get('thumbnail'),
    });

    if (!result.success) {
        return json(null, { status: 400 });
    }

    try {
        const response = await updateConversationThumbnail({
            context,
            conversationID: result.data.conversationID,
            professionalID: result.data.professionalID,
            thumbnail: result.data.thumbnail,
        });

        if (!response) {
            return json(null, { status: 500 });
        }

        return json({
            conversation: response.conversation,
            systemMessages: response.systemMessages,
        });
    } catch (error) {
        logger.error(`Error in ${UPDATE_CONVERSATION_THUMBNAIL_API}`, {
            error,
            data: result.data,
        });
        return json(null, { status: 500 });
    }
};
